import { AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import PaginatedResponse from '@/types/paginatedResponse';
import PersonStaffRelationship, {
  PersonStaffRelationshipType
} from '@/types/personStaffRelationship';
import { QueryParams } from '@/types/query';
import ajax from '@/utils/ajax';

export const personStaffRelationshipRepository = {
  BASE_PATH: '/api/dugout/person_staff_relationship/',

  async getPersonStaffRelationship({
    host,
    personStaffRelationshipId
  }: {
    host: string;
    personStaffRelationshipId: string;
  }): Promise<PersonStaffRelationship> {
    const path = `${this.BASE_PATH}${personStaffRelationshipId}/`;
    const response = await ajax.get({
      url: `${host}${path}`
    });

    return response.data;
  },

  async getPersonStaffRelationships({
    host,
    params
  }: {
    host: string;
    params: QueryParams;
  }): Promise<PaginatedResponse<PersonStaffRelationship[]>> {
    const path = this.BASE_PATH;
    const response = await ajax.get({
      url: `${host}${path}`,
      params
    });

    return response.data;
  },

  async createPersonStaffRelationship({
    host,
    params
  }: {
    host: string;
    params: Partial<PersonStaffRelationship>;
  }): Promise<PersonStaffRelationship> {
    const path = this.BASE_PATH;
    const response = await ajax.post<
      Partial<PersonStaffRelationship>,
      AxiosResponse<PersonStaffRelationship>
    >({
      url: `${host}${path}`,
      data: params
    });

    return response.data;
  },

  async updatePersonStaffRelationship({
    host,
    personStaffRelationshipId,
    params
  }: {
    host: string;
    personStaffRelationshipId: string;
    params: Partial<PersonStaffRelationship>;
  }): Promise<PersonStaffRelationship> {
    const path = `${this.BASE_PATH}${personStaffRelationshipId}/`;
    const response = await ajax.patch<
      Partial<PersonStaffRelationship>,
      AxiosResponse<PersonStaffRelationship>
    >({
      url: `${host}${path}`,
      data: params
    });

    return response.data;
  },

  async deletePersonStaffRelationship({
    host,
    personStaffRelationshipId
  }: {
    host: string;
    personStaffRelationshipId: string;
  }): Promise<void> {
    const path = `${this.BASE_PATH}${personStaffRelationshipId}/`;
    await ajax.delete({
      url: `${host}${path}`,
      data: {}
    });
  },

  async getPersonStaffRelationshipTypes({
    host
  }: {
    host: string;
  }): Promise<PaginatedResponse<PersonStaffRelationshipType[]>> {
    const path = `${this.BASE_PATH}list_types/`;
    const response = await ajax.get({
      url: `${host}${path}`
    });

    return response.data;
  }
};

const PersonStaffRelationshipRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getPersonStaffRelationship(
      personStaffRelationshipId: string
    ): Promise<PersonStaffRelationship> {
      return personStaffRelationshipRepository.getPersonStaffRelationship({
        host,
        personStaffRelationshipId
      });
    },

    async getPersonStaffRelationships(
      params: QueryParams
    ): Promise<PaginatedResponse<PersonStaffRelationship[]>> {
      return personStaffRelationshipRepository.getPersonStaffRelationships({
        host,
        params
      });
    },

    async createPersonStaffRelationship(
      params: Partial<PersonStaffRelationship>
    ): Promise<PersonStaffRelationship> {
      return personStaffRelationshipRepository.createPersonStaffRelationship({
        host,
        params
      });
    },

    async updatePersonStaffRelationship(
      personStaffRelationshipId: string,
      params: Partial<PersonStaffRelationship>
    ): Promise<PersonStaffRelationship> {
      return personStaffRelationshipRepository.updatePersonStaffRelationship({
        host,
        personStaffRelationshipId,
        params
      });
    },

    async deletePersonStaffRelationship(personStaffRelationshipId: string): Promise<void> {
      return personStaffRelationshipRepository.deletePersonStaffRelationship({
        host,
        personStaffRelationshipId
      });
    },

    async getPersonStaffRelationshipTypes(): Promise<
      PaginatedResponse<PersonStaffRelationshipType[]>
    > {
      return personStaffRelationshipRepository.getPersonStaffRelationshipTypes({ host });
    }
  };
};

export default PersonStaffRelationshipRepository;
